import type { DesktopScannerAPI, ScannerOptions } from '@worki/shared';
export type ScannerReceiver = (file: File, signal?: AbortSignal) => void | Promise<unknown>;
export interface ScanOrigin { account: string; context: string; href: string; kind: 'library' | 'invoices' | 'signing' | 'other'; folderId?: string }
export interface ScannerJob { id: string; origin: ScanOrigin; options: ScannerOptions; phase: 'review' | 'scan' | 'saving' | 'ready' | 'error' | 'done'; visible: boolean; review?: boolean; file?: File; error?: string }
/** One session-bound in-memory PDF. Its lifetime does not depend on a dialog or route. */
export class ScannerJobs {
  private account: string | null = null;
  private job: ScannerJob | null = null;
  private bridge?: DesktopScannerAPI;
  private deliveryAbort?: AbortController;
  private receiver?: () => ScannerReceiver | null;
  private listeners = new Set<() => void>();
  getSnapshot = (): ScannerJob | null => this.job;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private syncPending() { if (typeof window !== 'undefined') window.__workiPendingScan = Boolean(this.job && this.job.phase !== 'done'); }
  private emit(update: Partial<ScannerJob>) { if (this.job) this.job = { ...this.job, ...update }; this.syncPending(); for (const listener of this.listeners) listener(); }
  setAccount(account: string | null) { if (this.account !== account) { void this.cancel(); this.account = account; } }
  start(bridge: DesktopScannerAPI, options: ScannerOptions, origin: ScanOrigin, receiver: () => ScannerReceiver | null, review = false) {
    if (typeof window !== 'undefined' && window.__workiUpdateInstalling) throw new Error('scanner.errors.busy');
    if (this.job && this.job.phase !== 'done') throw new Error('scanner.errors.busy');
    if (origin.account !== this.account) throw new Error('scanner.errors.canceled');
    this.bridge = bridge; this.receiver = receiver;
    this.job = { id: crypto.randomUUID(), options: { ...options }, origin: { ...origin }, phase: 'scan', visible: true, review };
    this.emit({});
    void this.acquire(this.job);
  }
  private async acquire(job: ScannerJob) {
    try {
      const result = await this.bridge!.scan(job.id, job.options);
      if (!this.owns(job)) return;
      if (!result.ok) { this.emit({ phase: 'error', error: `scanner.errors.${result.code}` }); return; }
      const file = new File([new Uint8Array(result.value)], `scan-${new Date().toISOString().replace(/[:.]/g, '-')}.pdf`, { type: 'application/pdf' });
      this.emit({ file, phase: job.review ? 'review' : 'ready' });
      if (job.review) return;
      await this.deliver(job.id);
    } catch { if (this.owns(job)) this.emit({ phase: 'error', error: 'scanner.errors.scanFailed' }); }
  }
  private owns(job: Pick<ScannerJob, 'id' | 'origin'>) { return this.job?.id === job.id && this.account === job.origin.account; }
  private async deliver(id: string, receiver = this.receiver?.()) {
    const job = this.job;
    if (!job || job.id !== id || !this.owns(job) || !job.file || !receiver || job.phase === 'saving') return;
    const abort = new AbortController(); this.deliveryAbort = abort;
    this.emit({ phase: 'saving', error: undefined });
    try {
      await receiver(job.file, abort.signal);
      if (this.owns(job)) { this.receiver = undefined; this.deliveryAbort = undefined; this.emit({ phase: 'done', visible: false, file: undefined }); }
    } catch { if (this.owns(job)) this.emit({ phase: 'error', error: 'scanner.saveFailed' }); }
  }
  async confirmPages(file: File, expectedId: string) {
    const job = this.job;
    if (!job || job.id !== expectedId || job.phase !== 'review' || !this.owns(job)) return;
    if (file.type !== 'application/pdf' || file.size > 15 * 1024 * 1024) throw new Error('scanner.errors.tooLarge');
    this.emit({ file, phase: 'ready', visible: false });
    await this.deliver(job.id);
  }
  async retry(receiver?: ScannerReceiver) {
    const job = this.job;
    if (job?.file && this.owns(job)) await this.deliver(job.id, receiver ?? this.receiver?.());
  }
  hide() { this.emit({ visible: false }); }
  show() { this.emit({ visible: true }); }
  async cancel() {
    const old = this.job; const bridge = this.bridge;
    this.deliveryAbort?.abort(); this.deliveryAbort = undefined;
    this.job = null; this.receiver = undefined; this.bridge = undefined;
    this.syncPending();
    for (const listener of this.listeners) listener();
    if (old?.phase === 'scan') await bridge?.cancel(old.id).catch(() => undefined);
  }
}
export const scannerJobs = new ScannerJobs();
